import { Helmet } from "react-helmet-async";
import PageHeader from "../../components/PageHeader";
import { filter } from "lodash";
import Papa from "papaparse";
import { sentenceCase } from "change-case";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CModel from "../../components/CModel/CModel";
// @mui
import {
  Card,
  Stack,
  Paper,
  Button,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Breadcrumbs,
  Modal,
  IconButton,
  ButtonBase,
} from "@mui/material";

// components
import Label from "../../components/label";
import Iconify from "../../components/iconify";
import Scrollbar from "../../components/scrollbar";
import BulkDeleteConfirmDialog from "../../components/BulkDeleteConfirmDialog/BulkDeleteConfirmDialog";
// sections
import { UserListHead, UserListToolbar } from "../../sections/@dashboard/user";
// mock
import { getUsers } from "../../feature/userSlice";
import { useSelector } from "react-redux";
// import { deleteInventory, getInventory } from '../../feature/inventorySlice';
import {
  deleteTasks,
  getRejectionReasons,
  getSingleTasks,
  getTasks,
} from "../../feature/tasksSlice";
import { getJobs } from "../../feature/jobSlice";
import ApiCall from "../../utils/apicall";
import { HomeRounded } from "@mui/icons-material";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { isPdfUrl } from "../../utils/isPdfUrl";
import { AuthedImage, AuthedObject, openAuthedMedia, downloadAuthedMedia } from "../../components/AuthedMedia/AuthedMedia";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import SmartTable from "../../components/SmartTable/SmartTable";
// import ConformationModel from '../../components/CModel/ConformationModel';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "image", label: "Image", alignRight: false },
  // CR-2026-AISC-TASK-PMK Stage 2B (2026-04-28): the legacy `pmkNumber`
  // field is actually a 6-char auto-generated slug (UUID prefix), not a
  // piece-mark. Renamed the column header to "Task ID" to reflect what
  // it actually is. The new "PMK" column below shows `pmk_number`, the
  // AISC-canonical piece-mark backfilled in Stage 1 from description.
  { id: "pmk_number", label: "PMK", alignRight: false },
  { id: "heatNo", label: "Heat Number", alignRight: false },
  { id: "description", label: "Description", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  // { id: 'comments', label: 'Comments', alignRight: false },
  { id: "" },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function Tasks(props) {
  const dispatch = useDispatch();
  const open = null;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [conformation, setConformation] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");
  const [openModel, setOpenModel] = useState(false);
  const [openCallModel, setOpenCallModel] = useState(false);
  const [openUpdateModel, setOpenUpdateModel] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const { tasks, isTaskLoading, rejectionReasons } = useSelector(
    (state) => state.tasksSlice
  );
  const { jobs } = useSelector((state) => state.jobSlice);
  const { usersList, loginUser } = useSelector((state) => state.userSlice);
  const [allTasks, setAllTasks] = useState([]);
  const { permissions: userPermissions } = loginUser?.decodedToken ?? {}; // review-fix F-06: no crash on logout
  const canAddTask = hasPermission(userPermissions, PERMISSIONS.ADD_TASK);
  const canEditTask = hasPermission(userPermissions, PERMISSIONS.EDIT_TASK);
  const canDeleteTask = hasPermission(userPermissions, PERMISSIONS.DELETE_TASK);
  const canExportTask = hasPermission(userPermissions, PERMISSIONS.EXPORT_TASK);
  const canViewTaskList = hasPermission(userPermissions, PERMISSIONS.VIEW_TASK);
  const canViewWhiteboard = hasPermission(
    userPermissions,
    PERMISSIONS.COLLABORATE_ON_MICROSOFT_WHITEBOARD
  );
  const canDownloadDiagram = hasPermission(
    userPermissions,
    PERMISSIONS.Download_Diagram
  );
  const canMakeCall = hasPermission(userPermissions, PERMISSIONS.Make_Call);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  // Bulk-delete dialog state (CR-2026-AISC-TASK-BULK-DELETE)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const openFullscreen = (image) => {
    setFullscreenImage(image);
    setIsFullscreenOpen(true);
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    setIsFullscreenOpen(false);
  };
  // Note: previously fetched /task/export on mount (Codex CRITICAL — pulled
  // export-grade dataset for non-export users). Fix: defer until the user
  // clicks Export AND has EXPORT_TASK permission. Backend must enforce too.
  const fetchTasks = async () => {
    if (!canExportTask) return null;
    try {
      const response = await ApiCall.get("/task/export");
      setAllTasks(response.data.data);
      return response.data.data;
    } catch (error) {
      // Codex WARNING fix: previously console.log only — no user feedback.
      // Surface the failure so the operator knows their click did something
      // and what went wrong.
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch tasks for export";
      showErrorToast(msg);
      // eslint-disable-next-line no-console
      console.error("Error fetching tasks:", error);
      return null;
    }
  };

  const handleExport = async () => {
    if (!canExportTask) {
      showErrorToast("You don't have permission to export tasks.");
      return;
    }
    let data = allTasks;
    if (!data || data.length === 0) {
      data = await fetchTasks();
      if (!data) return;
    }
    if (data.length === 0) {
      showErrorToast("No tasks available to export.");
      return;
    }
    const customCsvData = data.map((task) => ({
      pmkNumber: task.pmkNumber,
      heatNo: task.heatNo,
      userName: task.userName,
      JobName: task.JobName,
      estimatedHour: task.estimatedHour,
      description: task.description,
      status: task.status,
      comments: task.comments,
      projectManager: task.projectManager,
      QCI: task.QCI,
      fitter: task.fitter,
      welder: task.welder,
      painter: task.painter,
      foreman: task.foreman,
      startedAt: task.startedAt,
      completedAt: task.completedAt,
    }));

    try {
      const csvData = Papa.unparse(customCsvData, { header: true });
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tasks.csv";
      a.click();
      URL.revokeObjectURL(url);
      showSuccessToast(`Exported ${data.length} task${data.length === 1 ? "" : "s"} to tasks.csv`);
    } catch (err) {
      showErrorToast("Couldn't generate the CSV file. " + (err?.message || ""));
      // eslint-disable-next-line no-console
      console.error("CSV export failed:", err);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    // CR-2026-FE-TASKS-PAGE-SCOPED-SELECT-ALL — page-scoped select-all.
    // Standard MUI / Gmail pattern: the header checkbox toggles only the rows
    // visible on the current pagination page. Selections from other pages are
    // preserved so the user can paginate + multi-page-select if they want.
    // Bonus: this prevents accidentally hitting the backend's 100-id cap when
    // a tenant has >100 total tasks.
    const visiblePageIds = visiblePageRows
      .map((n) => n.id)
      .filter(Boolean);

    if (event.target.checked) {
      // Add visible-page ids to the existing selection (preserving other pages)
      const newSet = new Set([...selected, ...visiblePageIds]);
      setSelected([...newSet]);
    } else {
      // Remove visible-page ids from the selection (preserving other pages)
      const removeSet = new Set(visiblePageIds);
      setSelected(selected.filter((id) => !removeSet.has(id)));
    }
  };

  // CR-2026-FE-TASKS-CHECKBOX-SELECTION — toggle a single row's selection.
  // Standard MUI per-row checkbox semantics: if the id is in the selected
  // array, remove it; if not, add it. Splices the array around the toggled
  // id without mutating.
  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  function applySortFilter(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    if (query) {
      return filter(
        tasks,
        // review-fix F-10: guard null pmkNumber so typing in search can't crash the Tasks list.
        (_inv) =>
          (_inv.pmkNumber || "").toLowerCase().indexOf(query.toLowerCase()) !== -1
      );
    }
    return stabilizedThis.map((el) => el[0]);
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tasks.length) : 0;

  const filteredUsers = applySortFilter(
    tasks,
    getComparator(order, orderBy),
    filterName
  );

  // CR-2026-FE-TASKS-PAGE-SCOPED-SELECT-ALL — the rows actually rendered for
  // the current pagination page. Used by:
  //   (1) handleSelectAllClick to toggle only this page's rows, and
  //   (2) the UserListHead numSelected/rowCount props so the header checkbox
  //       indeterminate/checked state reflects the visible page (not the
  //       entire dataset, which would never resolve to "checked" once a
  //       tenant has more than rowsPerPage tasks).
  const visiblePageRows = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const visiblePageIds = visiblePageRows
    .map((n) => n.id)
    .filter(Boolean);
  const visiblePageSelectedCount = visiblePageIds.filter((id) =>
    selected.includes(id)
  ).length;

  const isNotFound = !filteredUsers.length && !!filterName;

  const handleDelete = (id) => {
    dispatch(deleteTasks(deleteId)).then((res) => {
      if (res.type === "deleteTasks/tasks/fulfilled") {
        setConformation(false);
      }
    });
  };

  // CR-2026-FE-TASKS-CHECKBOX-SELECTION — selection now keys on task.id
  // directly. The BulkDeleteConfirmDialog renders PMK + Heat# + description
  // from these objects; handleBulkDelete pulls task.id straight off them.
  // Closes F5 from PR #11 review.
  const selectedTaskObjects = tasks.filter((t) => selected.includes(t.id));

  const handleBulkDelete = async () => {
    const ids = selectedTaskObjects.map((t) => t.id).filter(Boolean);
    if (ids.length === 0) {
      showErrorToast("No tasks selected.");
      return;
    }
    setBulkDeleteLoading(true);
    try {
      // CR-2026-AISC-TASK-BULK-DELETE + CR-2026-FE-BULK-DELETE-HOTFIX
      // ApiCall.baseURL is REACT_APP_URL = "https://shopfloormgmt.com/api".
      // Path is /task/ (singular) to match the existing convention in
      // tasksSlice.js. Backend caps at 100 ids/request and returns
      // { deleted_count, skipped_count }.
      const res = await ApiCall.post("/task/bulk-delete", { ids });
      const data = res?.data?.data || {};
      const deleted = data.deleted_count ?? ids.length;
      const skipped = data.skipped_count ?? 0;
      const msg =
        skipped > 0
          ? `${deleted} task${deleted === 1 ? "" : "s"} deleted (${skipped} skipped — already deleted)`
          : `${deleted} task${deleted === 1 ? "" : "s"} deleted`;
      showSuccessToast(msg);
      setSelected([]);
      setBulkDeleteOpen(false);
      // Refresh the table from the source of truth (Redux thunk)
      dispatch(getTasks());
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Bulk delete failed. Please try again.";
      showErrorToast(apiMsg);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleOpenModel = () => {
    setOpenModel(!open);
  };

  useEffect(() => {
    dispatch(getTasks());
    dispatch(getJobs());
    dispatch(getUsers());
    dispatch(getRejectionReasons());
  }, [dispatch]);

  // S-68: download through the authenticated fetch. The old plain fetch() sent no
  // Bearer header, so with the upload gate on it saved the 401 JSON as
  // "image.jpeg"; it also never revoked the object URL. The file keeps its own
  // name and extension (the drawing PDF is a .pdf, not a .jpeg).
  const handleDownload = async (image) => {
    const ok = await downloadAuthedMedia(image);
    if (!ok) showErrorToast("Couldn't download the file. Please sign in again or try later.");
  };
  const handleCollaborate = () => {
    const whiteboardUrl =
      "https://apps.apple.com/us/app/microsoft-whiteboard/id1352499399";
    window.open(whiteboardUrl, "_blank");
  };

  const handleOpenCallModel = () => {
    setOpenCallModel(!open);
  };
  return (
    <>
      <Helmet>
        <title>Tasks | SFM</title>
      </Helmet>
      {openModel ? (
        <CModel
          users={usersList}
          isLoading={isTaskLoading}
          open={openModel}
          setOpen={setOpenModel}
          jobs={jobs}
          filter={"add-task"}
        />
      ) : (
        ""
      )}
      {openUpdateModel ? (
        <CModel
          open={openUpdateModel}
          isLoading={isTaskLoading}
          data={selectedRow}
          setOpen={setOpenUpdateModel}
          filter={"edit-task"}
          jobs={jobs}
          users={usersList}
          reasons={rejectionReasons}
        />
      ) : (
        ""
      )}
      {conformation ? (
        <CModel
          filter={"conformation"}
          isLoading={isTaskLoading}
          open={conformation}
          setOpen={setConformation}
          handleClick={handleDelete}
          title="Are you sure?"
        />
      ) : (
        ""
      )}

      {/* Bulk-delete confirmation dialog (CR-2026-AISC-TASK-BULK-DELETE).
          Mounted unconditionally; visibility is controlled via the open prop
          so the type-DELETE input doesn't accidentally retain stale state
          across opens. */}
      <BulkDeleteConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        selectedTasks={selectedTaskObjects}
        onConfirm={handleBulkDelete}
        isLoading={bulkDeleteLoading}
      />
      {canViewTaskList && (
        <Container>
          <PageHeader
            breadcrumbs={[{ label: "Tasks" }]}
            title="Tasks"
            action={<div style={{ display: "flex", gap: "10px" }}>
              {canExportTask && (
                <Button
                  onClick={handleExport}
                  variant="outlined"
                  startIcon={<Iconify icon="ph:download-fill" />}
                >
                  Export Tasks
                </Button>
              )}
              {canAddTask && (
                <Button
                  onClick={handleOpenModel}
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  New Task
                </Button>
              )}
            </div>}
            guide={{
              why: "Give welders and QC one place to see what to build, from which drawing, and at what status — so no one works off the wrong print.",
              who: "Welders and QC inspectors on the shop floor; foremen assigning the work.",
              what: "The live, piece-by-piece work list: piece marks, heat numbers, status, and the controlled drawing for each task.",
              how: "Open a task to view its drawing, advance its status as work completes, and acknowledge the drawing revision.",
              when: "Every shift, on the shop floor — this is the day-to-day welder and QC working view.",
              standard: "AISC §1.7.0 fabrication workflow",
            }}
          />

          <Card>
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <UserListToolbar
                label={"Search tasks..."}
                numSelected={selected.length}
                filterName={filterName}
                onFilterName={handleFilterByName}
              />

              {/* Bulk-delete button: visible only when 1+ tasks are selected
                  AND the user has DELETE_TASK permission. CR-2026-AISC-TASK-
                  BULK-DELETE — opens BulkDeleteConfirmDialog with type-DELETE
                  confirmation against the soft-delete endpoint. ApiCall path
                  resolves via REACT_APP_URL base — see handleBulkDelete. */}
              {selected.length > 0 && canDeleteTask && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Iconify icon="eva:trash-2-outline" />}
                  onClick={() => setBulkDeleteOpen(true)}
                  sx={{ mr: 2, whiteSpace: "nowrap" }}
                  aria-label={`Delete ${selected.length} selected task${selected.length === 1 ? "" : "s"}`}
                >
                  Delete {selected.length} Selected
                </Button>
              )}

              <TablePagination
                rowsPerPageOptions={[10, 25]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <SmartTable tableId="tasks" lockedColumns={["PMK", "Heat Number", "Status"]}>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    /* CR-2026-FE-TASKS-PAGE-SCOPED-SELECT-ALL — pass the
                       VISIBLE PAGE counts, not the entire dataset, so the
                       header checkbox shows checked when this page is fully
                       selected (not "indeterminate forever" once tasks > 10). */
                    rowCount={visiblePageIds.length}
                    numSelected={visiblePageSelectedCount}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    /* CR-2026-FE-TASKS-CHECKBOX-SELECTION — opt-in row
                       selection. Only renders the checkbox column on Tasks;
                       UserListHead's other 8 consumer pages render unchanged
                       because they don't pass this prop. */
                    enableSelection={canDeleteTask}
                  />
                  <TableBody>
                    {filteredUsers
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => {
                        const {
                          id,
                          pmkNumber,
                          heatNo,
                          pmk_number,
                          description,
                          status,
                          image,
                          diagram,
                        } = row;
                        // CR-2026-FE-TASKS-CHECKBOX-SELECTION — per-row
                        // selected state, sourced from task.id (integer PK).
                        const isItemSelected = selected.indexOf(id) !== -1;
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role={canDeleteTask ? "checkbox" : undefined}
                            aria-checked={canDeleteTask ? isItemSelected : undefined}
                            selected={canDeleteTask && isItemSelected}
                          >
                            {/* CR-2026-FE-TASKS-CHECKBOX-SELECTION — per-row
                                selection cell. Gated on the same DELETE_TASK
                                permission as the bulk-delete button so users
                                without permission don't see useless UI. */}
                            {canDeleteTask && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  onChange={(event) => handleClick(event, id)}
                                  inputProps={{
                                    "aria-label": `select task ${pmkNumber || id}`,
                                  }}
                                />
                              </TableCell>
                            )}
                            {/* <TableCell align="left">
                              {image ? (
                                <img
                                  alt="task"
                                  style={{ width: "50px", height: "40px" }}
                                  src={image}
                                  onClick={() => openFullscreen(image)}
                                />
                              ) : (
                                "_"
                              )}
                            </TableCell> */}
                            <TableCell align="left">
                              {image ? (
                                <>
                                  {isPdfUrl(image) ? (
                                    // Codex WARNING fix: was a styled <div role="button"> with custom
                                    // keyDown handling and no visible focus state. Replaced with MUI
                                    // ButtonBase so Enter/Space, focus ring, and ripple come for free.
                                    <ButtonBase
                                      onClick={() => openAuthedMedia(image)}
                                      aria-label="Open drawing PDF in new tab"
                                      title="Drawing (PDF) — click to open"
                                      sx={{
                                        width: 50,
                                        height: 40,
                                        background: "#fdf1d6",
                                        color: "#7c4a03",
                                        border: "1px solid #f1d28b",
                                        borderRadius: "4px",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: "0.5px",
                                        "&:focus-visible": {
                                          outline: "2px solid #2065d1",
                                          outlineOffset: 2,
                                        },
                                      }}
                                    >
                                      PDF
                                    </ButtonBase>
                                  ) : (
                                    <AuthedImage
                                      alt="task"
                                      style={{
                                        width: "50px",
                                        height: "40px",
                                        objectFit: "cover",
                                        cursor: diagram ? "pointer" : "zoom-in",
                                        borderRadius: "4px",
                                        border: "1px solid #e0e0e0",
                                      }}
                                      src={image}
                                      title={diagram ? "Click to open drawing PDF" : "Click to enlarge"}
                                      onClick={() =>
                                        diagram
                                          ? openAuthedMedia(diagram)
                                          : openFullscreen(image)
                                      }
                                    />
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      marginLeft: "-15px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {canMakeCall && (
                                      <IconButton
                                        onClick={() => handleOpenCallModel()}
                                      >
                                        <Iconify
                                          icon="material-symbols:call"
                                          style={{
                                            height: "1rem",
                                            width: "1rem",
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                    {openCallModel ? (
                                      <CModel
                                        open={openCallModel}
                                        setOpen={setOpenCallModel}
                                        filter={"call-list"}
                                      />
                                    ) : (
                                      ""
                                    )}
                                    {canDownloadDiagram && (
                                      <IconButton
                                        onClick={() => handleDownload(diagram || image)}
                                      >
                                        <Iconify
                                          icon="material-symbols:download"
                                          style={{
                                            height: "1rem",
                                            width: "1rem",
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                    {canViewWhiteboard && (
                                      <IconButton
                                        onClick={() => handleCollaborate()}
                                      >
                                        <Iconify
                                          icon="carbon:collaborate"
                                          style={{
                                            height: "1rem",
                                            width: "1rem",
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                  </div>
                                </>
                              ) : (
                                "_"
                              )}
                            </TableCell>


                            <TableCell align="left">
                              {pmk_number ? (
                                <Typography
                                  variant="subtitle2"
                                  noWrap
                                  sx={{ fontWeight: 600 }}
                                >
                                  {pmk_number}
                                </Typography>
                              ) : (
                                "_"
                              )}
                            </TableCell>
                            <TableCell align="left">{heatNo}</TableCell>
                            <TableCell align="left">
                              {description ? description : "_"}
                            </TableCell>
                            <TableCell align="left">
                              <Label
                                color={
                                  status === "approved"
                                    ? "success"
                                    : status === "in_process"
                                    ? "default"
                                    : status === "pending"
                                    ? "warning"
                                    : "error"
                                }
                              >
                                {sentenceCase(status)}
                              </Label>
                            </TableCell>

                            <TableCell
                              align="right"
                              style={{ display: "flex", marginTop: "0px" }}
                            >
                              {canEditTask && (
                                <MenuItem
                                  key={id}
                                  onClick={() => {
                                    dispatch(getSingleTasks(id)).then((res) => {
                                      setSelectedRow(row);
                                      setOpenUpdateModel(!openUpdateModel);
                                    });
                                  }}
                                  style={{ marginTop: "25px" }}
                                >
                                  <Iconify icon={"eva:edit-fill"} />
                                </MenuItem>
                              )}
                              {canDeleteTask && (
                                <MenuItem
                                  sx={{ color: "error.main" }}
                                  onClick={() => {
                                    setConformation(true);
                                    setDeleteId(id);
                                  }}
                                  style={{ marginTop: "25px" }}
                                >
                                  <Iconify icon={"eva:trash-2-outline"} />
                                </MenuItem>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>

                  {isNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <Paper
                            sx={{
                              textAlign: "center",
                            }}
                          >
                            <Typography variant="h6" paragraph>
                              Not found
                            </Typography>

                            <Typography variant="body2">
                              No results found for &nbsp;
                              <strong>&quot;{filterName}&quot;</strong>.
                              <br /> Try checking for typos or using complete
                              words.
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </SmartTable>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Container>
      )}
      <Modal
        open={isFullscreenOpen}
        onClose={closeFullscreen}
        aria-labelledby="image-modal"
        aria-describedby="image-modal-description"
      >
        <div>
          <button
            onClick={closeFullscreen}
            style={{
              position: "absolute",
              top: "8%",
              right: "3%",
              padding: "10px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "black",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            <Iconify icon="mingcute:close-fill" />
          </button>

          {isPdfUrl(fullscreenImage) ? (
            <AuthedObject
              src={fullscreenImage}
              ariaLabel="drawing PDF fullscreen preview"
              style={{ width: "100%", height: "600px", marginTop: "25px" }}
            />
          ) : (
            <AuthedImage
              alt="fullscreen"
              style={{ width: "100%", height: "600px", marginTop: "25px" }}
              src={fullscreenImage}
            />
          )}
        </div>
      </Modal>
    </>
  );
}
